import { Fragment } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { IconName } from '@optimus-web/core';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { ApplicationState } from '@mews-commander/types';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import {
    hasCurrentEnterpriseSmsIntegration,
    getCurrentEnterprise,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { hasCurrentEnterpriseServiceProviderMode } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    isCurrentProfileAdmin,
    hasCurrentProfilePlatformScope,
    canCurrentProfileManageMailQueue,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { ExtendedEnterprise } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { createListItemWithLink } from '../helpers';
import { SidebarExpandableListItem, SidebarFontIcon } from '../style';

interface StateProps {
    isAdmin: boolean;
    canAccessAllEnterprises: boolean;
    hasServiceProviderMode: boolean;
    hasSmsIntegration: boolean;
    currentEnterprise: ExtendedEnterprise;
    canManageMailQueue: boolean;
    hasPlatformScope: boolean;
}

interface OwnProps {
    value: string;
}

const EnterpriseQueueMenu = ({
    currentEnterprise,
    isAdmin,
    canAccessAllEnterprises,
    hasServiceProviderMode,
    hasPlatformScope,
    hasSmsIntegration,
    canManageMailQueue,
    ...other
}: StateProps & OwnProps) => {
    const isAdminOrPlatformAdmin = isAdmin || (hasPlatformScope && canAccessAllEnterprises);
    const textMessagingConfigurationEnabled = useFeatureFlag(FeatureFlag.textMessagingConfigurationEnabled);
    const exportViewsV2Enabled = useFeatureFlag(FeatureFlag.exportViewsV2Enabled);

    const textMessageQueue =
        textMessagingConfigurationEnabled && hasSmsIntegration
            ? createListItemWithLink({
                  textKey: TextKey.TextMessagePlural,
                  iconName: IconName.MessagesPlural,
                  path: 'TextMessageQueueItem/Index',
              })
            : null;

    return hasServiceProviderMode && currentEnterprise.id ? (
        <SidebarExpandableListItem
            leftIcon={<SidebarFontIcon value={IconName.Queues} />}
            primaryText={<LocalizedText textKey={TextKey.QueuePlural} />}
            {...other}
        >
            {createListItemWithLink({
                textKey: TextKey.DeviceCommandPlural,
                iconName: IconName.DeviceCommand,
                path: 'DeviceCommand/Index',
            })}
            {!isAdminOrPlatformAdmin && canManageMailQueue ? (
                <>
                    {createListItemWithLink({
                        textKey: TextKey.MailPlural,
                        iconName: IconName.Mails,
                        path: 'MailQueueItem/Index',
                    })}

                    {textMessageQueue}
                </>
            ) : null}
            {isAdminOrPlatformAdmin && (
                <Fragment>
                    {createListItemWithLink({
                        textKey: TextKey.ChannelManager,
                        iconName: IconName.Languages,
                        path: 'ChannelManagerQueueItem/Index',
                    })}
                    {!exportViewsV2Enabled &&
                        createListItemWithLink({
                            textKey: TextKey.ExportPlural,
                            iconName: IconName.Exports,
                            path: 'Export/Queue',
                        })}
                    {createListItemWithLink({
                        textKey: TextKey.MailPlural,
                        iconName: IconName.Mails,
                        path: 'MailQueueItem/Index',
                    })}

                    {textMessageQueue}

                    {createListItemWithLink({
                        textKey: TextKey.InstallationPlural,
                        iconName: IconName.Installations,
                        path: 'InstallationQueueItem/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.ImportPlural,
                        iconName: IconName.Installations,
                        path: 'ImportQueueItem/Index',
                    })}
                    {createListItemWithLink({
                        textKey: TextKey.InventoryUpdatePlural,
                        iconName: IconName.InventoryUpdates,
                        path: 'InventoryUpdate/Index',
                    })}
                </Fragment>
            )}
        </SidebarExpandableListItem>
    ) : null;
};

export default connect<StateProps, {}, OwnProps, ApplicationState>(
    createStructuredSelector<ApplicationState, StateProps>({
        isAdmin: isCurrentProfileAdmin,
        canAccessAllEnterprises: hasPlatformScopeWithAccessAllEnterprises,
        hasServiceProviderMode: hasCurrentEnterpriseServiceProviderMode,
        hasSmsIntegration: hasCurrentEnterpriseSmsIntegration,
        currentEnterprise: getCurrentEnterprise,
        canManageMailQueue: canCurrentProfileManageMailQueue,
        hasPlatformScope: hasCurrentProfilePlatformScope,
    })
)(EnterpriseQueueMenu);
